import { DEFAULT_SYSTEM_LANGUAGE } from '@/lib/constants';
import { ELocalStorageKeys, EPlatform, EPlatformType } from '@/lib/enums';
import { OfferData, OfferDataForPublisherEvent, PortalBootResponse, PortalDesign, SupportedLanguage } from '@/lib/types';
import { getUtmParams } from '@/lib/utils';

import { Observable } from './observable';
import { webStorageUtil } from './webStorage.state.service';

export const initialDesigns = [
  {
    topBarColor: '#911434',
    topBarTextColor: '#FFFFFF',
    logoTextColor: '#FFFFFF',
    logoSubtextColor: '#f6d822',
    backgroundColor: '#13406e',
    menuColor: '#911434',
    menuTextColor: '#FFFFFF',
    menuActiveTextColor: '#FFFFFF',
    menuHighlightColor: '#fc124e',
    menuBottomImage: 'https://i.imgur.com/yOM5O7h.png',
    mainContentCardImg:
      'https://e0.pxfuel.com/wallpapers/546/590/desktop-wallpaper-spider-man-ps4-good-ps4.jpg',
    contentCardTextColor: '#FFFFFF',
    contentCardBackgroundColor: '#13406e',
    buttonColor: '#FFFFFF',
    buttonTextColor: '#911434',
  },
  {
    topBarColor: '#121212',
    topBarTextColor: '#FFFFFF',
    logoTextColor: '#FFFFFF',
    logoSubtextColor: '#f6d822',
    backgroundColor: '#1e1e1e',
    menuColor: '#121212',
    menuTextColor: '#FFFFFF',
    menuActiveTextColor: '#FFFFFF',
    menuHighlightColor: '#272727',
    contentCardTextColor: '#FFFFFF',
    mainContentCardImg:
      'https://images.pexels.com/photos/194511/pexels-photo-194511.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    contentCardBackgroundColor: '#1e1e1e',
    buttonColor: '#FFFFFF',
    buttonTextColor: '#121212',
  },
];

export { DEFAULT_SYSTEM_LANGUAGE };

export interface IAppStateSeed {
  initialBoot: PortalBootResponse;
  deviceType: EPlatform;
  platform: EPlatformType;
  domain: string;
  currentLanguage: string;
}

/**
 * The portal's app state. One instance per browser document (the exported
 * singleton), and one per request on the server, where a shared instance would
 * let one publisher's boot leak into another publisher's HTML.
 */
export class AppStateService {
  readonly initialBoot = new Observable<PortalBootResponse | null>(null);
  readonly design = new Observable<PortalDesign>(initialDesigns[1]);
  readonly deviceType = new Observable<EPlatform>(EPlatform.DESKTOP || 'APPCHARGE');
  readonly platform = new Observable<EPlatformType>(EPlatformType.WEB);
  readonly currentPageName = new Observable<string>('');
  readonly domain = new Observable<string>('');
  // Off until something asks for it: the document must paint its content, not a
  // loader over it. The auto-collect flow and the login flows turn it on.
  readonly displayLoader = new Observable<boolean>(false);
  readonly showConfetti = new Observable<boolean>(false);
  readonly failedHashValidation = new Observable<boolean>(false);
  readonly selectedOffer = new Observable<OfferData | null | string>();
  // Seeded from storage and the URL, so declared with what the server renders.
  readonly isProfileEnabled = new Observable<boolean>(
    webStorageUtil.get(ELocalStorageKeys.INTEGRATION_PORFILE) !== null,
    { serverSnapshot: false }
  );
  readonly backgroundProps = new Observable<object>({});
  readonly collectedProductsImages = new Observable<string[]>([]);
  readonly infoModalUrl = new Observable<string | null>();
  readonly utms = new Observable<Record<string, string>>(getUtmParams(), { serverSnapshot: {} });
  readonly isGlobalLoading = new Observable<boolean>(false);
  readonly currentLanguage = new Observable<string | null>(null);
  readonly languagesList = new Observable<SupportedLanguage[]>([]);
  readonly campaignOfferData = new Observable<OfferDataForPublisherEvent | undefined>(undefined);
  readonly showPaymentModal = new Observable<boolean>(false);
  readonly activeOrderId = new Observable<string>('');

  clear = () => {
    this.displayLoader.set(false);
    this.design.set(initialDesigns[1]);
    this.failedHashValidation.set(false);
    this.showConfetti.set(false);
    this.selectedOffer.set(null);
    this.backgroundProps.set({});
    this.collectedProductsImages.set([]);
    this.infoModalUrl.set(null);
    this.utms.set({});
    this.deviceType.set(EPlatform.DESKTOP);
    this.platform.set(EPlatformType.WEB);
    this.currentPageName.set('');
    this.domain.set('');
    this.campaignOfferData.set(undefined);
    this.showPaymentModal.set(false);
    this.activeOrderId.set('');
    const savedLanguage = webStorageUtil.get(ELocalStorageKeys.CURRENT_LANGUAGE);
    this.currentLanguage.set(savedLanguage || DEFAULT_SYSTEM_LANGUAGE);

    this.languagesList.set([]);
  };

  setIsGlobalLoading = (isLoading: boolean) => this.isGlobalLoading.set(isLoading);
  setDisplayLoader = (showLoader: boolean) => this.displayLoader.set(showLoader);
  setShowConfetti = (show: boolean) => this.showConfetti.set(show);
  setSelectedOffer = (offer: OfferData | null | string) => this.selectedOffer.set(offer);
  setFailedHashValidation = (failed: boolean) => this.failedHashValidation.set(failed);
  setIsProfileEnabled = (showIntegrationProfiles: boolean) =>
    this.isProfileEnabled.set(showIntegrationProfiles);
  setBackgroundProps = (background: object) => this.backgroundProps.set(background);
  setCollectedProductsImages = (images: string[]) => this.collectedProductsImages.set(images);
  setInfoModalUrl = (url?: string) => this.infoModalUrl.set(url || null);
  setUtms = (utm?: Record<string, string>) => this.utms.set(utm || {});
  setInitialBoot = (boot: PortalBootResponse | null) => this.initialBoot.set(boot);
  setDeviceType = (deviceType: EPlatform) => this.deviceType.set(deviceType);
  setPlatform = (platform: EPlatformType) => this.platform.set(platform);
  setDesign = (design: PortalDesign) => this.design.set(design);
  setCurrentPageName = (pageName: string) => this.currentPageName.set(pageName);
  setDomain = (domain: string) => this.domain.set(domain);
  setCurrentLanguage = (currentLanguage: string) => this.currentLanguage.set(currentLanguage);
  setLanguagesList = (lngList: SupportedLanguage[]) => this.languagesList.set(lngList);
  setCampaignOfferData = (campaignOfferData: OfferDataForPublisherEvent | undefined) => this.campaignOfferData.set(campaignOfferData);
  setShowPaymentModal = (showPaymentModal: boolean) => this.showPaymentModal.set(showPaymentModal);
  setActiveOrderId = (activeOrderId: string) => this.activeOrderId.set(activeOrderId);
}

export const appStateService = new AppStateService();

/**
 * Puts what the server knows about the request into a state instance before
 * anything renders from it, so the server and the hydrating client render the
 * same document. Seeds once: the singleton survives client navigations.
 */
export const seedAppState = (service: AppStateService, seed: IAppStateSeed): void => {
  if (service.initialBoot.get() !== null) {
    return;
  }

  service.initialBoot.set(seed.initialBoot);
  service.deviceType.set(seed.deviceType);
  service.platform.set(seed.platform);
  service.domain.set(seed.domain);
  service.currentLanguage.set(seed.currentLanguage);
};
